import {
  Box,
  CircularProgress,
  InputAdornment,
  List,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ApplicationCount } from '@features/applications';
import ApplicationsCountsListItem from '@features/applications/components/ApplicationsCountsListItem/ApplicationsCountsListItem';
import { useGetApplicationsCounts } from '@features/applications/hooks/useGetApplicationsCounts';
import { white } from '@styles/colors';
import { authContext } from '@staffedupusa/shared-components';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { selectedPlanName } from '@utils/pricing';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';

type ApplicationsCountsListProps = {
  selectedPositions: string[];
  setSelectedPositions: Dispatch<SetStateAction<string[]>>;
  disableButtons: boolean;
  handlePageNo: (newPageNo: number) => void;
};

const ApplicationsCountsList: FC<ApplicationsCountsListProps> = ({
  selectedPositions,
  setSelectedPositions,
  disableButtons,
  handlePageNo,
}) => {
  const isXLarge = useMediaQuery('(min-width:1370px)');
  const { authData } = useContext(authContext);
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);
  const [searchValue, setSearchValue] = useState('');
  const { applicationsCountsData, applicationsCountsLoading } =
    useGetApplicationsCounts({ query: searchValue });

  const isMasterAccount = authData?.user?.master_account;
  const restrictedPositions =
    authData?.user?.permissions?.restricted_positions || [];

  const handleTogglePosition = (positionTitle: string) => {
    setSelectedPositions((prev) =>
      prev.includes(positionTitle)
        ? prev.filter((title) => title !== positionTitle)
        : [...prev, positionTitle],
    );
  };

  const getTotalApplicationsCount = useMemo(() => {
    return applicationsCountsData?.reduce(
      (acc, cur) => ({
        ...acc,
        unattended: acc.unattended + cur.unattended,
        messaged: acc.messaged + cur.messaged,
        attended: acc.attended + cur.attended,
        interview_process: acc.interview_process + cur.interview_process,
      }),
      {
        s_title: 'All',
        unattended: 0,
        messaged: 0,
        attended: 0,
        interview_process: 0,
      },
    );
  }, [applicationsCountsData]);

  const filteredData = useMemo(() => {
    return (
      applicationsCountsData?.filter((item) => {
        return (
          isMasterAccount ||
          !restrictedPositions.some(
            (restricted) => restricted.s_title === item.s_title,
          )
        );
      }) || []
    );
  }, [applicationsCountsData, isMasterAccount, restrictedPositions]);

  return (
    <Box bgcolor={white} borderRadius="8px" py={3} mb={2}>
      <Stack
        sx={{ flexDirection: isXLarge ? 'row' : 'column' }}
        justifyContent="space-between"
        alignItems={isXLarge ? 'center' : 'start'}
        px={3}
        mb={2}
      >
        <TextField
          variant="outlined"
          size="small"
          color="secondary"
          fullWidth
          placeholder="Search Position…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="secondary" sx={{ fontSize: '20px' }} />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment
                position="end"
                onClick={() => setSearchValue('')}
                sx={{ cursor: 'pointer' }}
              >
                <ClearIcon color="secondary" sx={{ fontSize: '18px' }} />
              </InputAdornment>
            ) : null,
          }}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          sx={{ '& input': { fontSize: '14px' }, padding: '10px' }}
        />
      </Stack>

      {applicationsCountsLoading ? (
        <List sx={{ textAlign: 'center', margin: '10px' }}>
          <CircularProgress />
        </List>
      ) : filteredData.length ? (
        <List>
          {filteredData.length > 1 && (
            <ApplicationsCountsListItem
              key="All"
              applicationCount={getTotalApplicationsCount as ApplicationCount}
              selected={!selectedPositions.length}
              handleTogglePosition={() => {
                if (authData?.user?.company) {
                  track(
                    `${amplitudeEventsName.APPLICANTS_CLICK_POSITION}-${selectedPlan}`,
                  );
                }
                setSelectedPositions([]);
              }}
              disabled={disableButtons}
              handlePageNo={handlePageNo}
            />
          )}
          {filteredData
            .sort((a, b) => a.s_title.localeCompare(b.s_title))
            .map((applicationCount) => (
              <ApplicationsCountsListItem
                key={applicationCount.s_title}
                applicationCount={applicationCount}
                selected={selectedPositions.includes(applicationCount.s_title)}
                handleTogglePosition={() => {
                  if (authData?.user?.company) {
                    track(
                      `${amplitudeEventsName.APPLICANTS_CLICK_POSITION}-${selectedPlan}`,
                    );
                  }
                  handleTogglePosition(applicationCount.s_title);
                }}
                disabled={disableButtons}
                handlePageNo={handlePageNo}
              />
            ))}
        </List>
      ) : (
        <Typography sx={{ textAlign: 'center', margin: '10px' }}>
          No Position Found
        </Typography>
      )}
    </Box>
  );
};

export default ApplicationsCountsList;
